import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';

interface RouteOptions {
  beforeNavigate?: () => Promise<void> | void;
  afterNavigate?: () => Promise<void> | void;
  replace?: boolean;
  scroll?: boolean;
}

type Params = Record<string, string | null>;

const useRoute = () => {
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);

  // 提取公共逻辑到工具函数
  const buildUrlWithParams = (path: string, params: Params) => {
    // 解析原始路径中的参数
    const [basePath, pathQuery] = path.split('?');
    // const currentParams = new URLSearchParams(window.location.search);
    const pathParams = new URLSearchParams(pathQuery);

    // 创建新的URL参数对象
    const mergedParams = new URLSearchParams({
      // ...Object.fromEntries(currentParams.entries()),
      ...Object.fromEntries(pathParams.entries()),
    });

    // 处理新参数，空值参数会被删除
    Object.entries(params).forEach(([key, value]) => {
      value === '' || value === null
        ? mergedParams.delete(key)
        : mergedParams.set(key, value);
    });

    return `${basePath}?${mergedParams.toString()}`;
  };

  const navigate = useCallback(
    async (path: string, params: Params = {}, options: RouteOptions = {}) => {
      const {
        beforeNavigate,
        afterNavigate,
        replace = false,
        ...rest
      } = options;

      try {
        setIsNavigating(true);

        // 执行跳转前的回调
        if (beforeNavigate) await beforeNavigate();

        const url = buildUrlWithParams(path, params);

        // 执行路由跳转
        replace ? router.replace(url, rest) : router.push(url, rest);

        // 执行跳转后的回调
        if (afterNavigate) await afterNavigate();
      } catch (error) {
        console.error('Navigation error:', error);
        throw error;
      } finally {
        setIsNavigating(false);
      }
    },
    [router]
  );

  const push = useCallback(
    (
      path: string,
      params: Params = {},
      options: Omit<RouteOptions, 'replace'> = {}
    ) => {
      return navigate(path, params, { ...options, replace: false });
    },
    [navigate]
  );

  const replace = useCallback(
    (
      path: string,
      params: Params = {},
      options: Omit<RouteOptions, 'replace'> = {}
    ) => {
      return navigate(path, params, { ...options, replace: true });
    },
    [navigate]
  );

  return {
    push,
    replace,
    back: router.back,
    isNavigating,
    navigate,
  };
};

export default useRoute;
